/**
 * 店舗管理APIクライアント
 */

import { AuthenticatedApiClient } from '../client';
import type {
  MiseSeller,
  MiseSellerGroup,
  MiseSellerSellerGroupMap,
  UpdateSellersNextRequest,
  UpdateSellerGroupsNextRequest,
  UpdateSellerSellerGroupMapNextRequest,
} from '@/lib/types/mise/seller';

/**
 * 集計後の店舗一覧を取得（認証付き）
 */
export async function getSellersCurrentAuthenticated(
  accountId: string,
  authHeaders: Record<string, string>,
): Promise<MiseSeller[]> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    const sellers = await client.get<MiseSeller[]>(`/mise/accounts/${accountId}/sellers/current`);
    return sellers;
  } catch (error) {
    console.error(
      `Failed to fetch current sellers for account ${accountId} (authenticated):`,
      error,
    );
    throw error;
  }
}

/**
 * 集計前の店舗一覧を取得（認証付き）
 */
export async function getSellersNextAuthenticated(
  accountId: string,
  authHeaders: Record<string, string>,
): Promise<MiseSeller[]> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    const sellers = await client.get<MiseSeller[]>(`/mise/accounts/${accountId}/sellers/next`);
    return sellers;
  } catch (error) {
    console.error(`Failed to fetch next sellers for account ${accountId} (authenticated):`, error);
    throw error;
  }
}

/**
 * 集計前の店舗一覧を更新（認証付き）
 */
export async function updateSellersNextAuthenticated(
  accountId: string,
  request: UpdateSellersNextRequest,
  authHeaders: Record<string, string>,
): Promise<void> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    await client.put(`/mise/accounts/${accountId}/sellers/next`, request);
  } catch (error) {
    console.error(`Failed to update next sellers for account ${accountId}:`, error);
    throw error;
  }
}

/**
 * 集計後の店舗グループ一覧を取得（認証付き）
 */
export async function getSellerGroupsCurrentAuthenticated(
  accountId: string,
  authHeaders: Record<string, string>,
): Promise<MiseSellerGroup[]> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    return await client.get<MiseSellerGroup[]>(`/mise/accounts/${accountId}/seller-groups/current`);
  } catch (error) {
    console.error(`Failed to fetch current seller groups for account ${accountId}:`, error);
    throw error;
  }
}

/**
 * 集計前の店舗グループ一覧を取得（認証付き）
 */
export async function getSellerGroupsNextAuthenticated(
  accountId: string,
  authHeaders: Record<string, string>,
): Promise<MiseSellerGroup[]> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    return await client.get<MiseSellerGroup[]>(`/mise/accounts/${accountId}/seller-groups/next`);
  } catch (error) {
    console.error(`Failed to fetch next seller groups for account ${accountId}:`, error);
    throw error;
  }
}

/**
 * 集計前の店舗グループ一覧を更新（認証付き）
 */
export async function updateSellerGroupsNextAuthenticated(
  accountId: string,
  request: UpdateSellerGroupsNextRequest,
  authHeaders: Record<string, string>,
): Promise<void> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    await client.put(`/mise/accounts/${accountId}/seller-groups/next`, request);
  } catch (error) {
    console.error(`Failed to update next seller groups for account ${accountId}:`, error);
    throw error;
  }
}

/**
 * 集計後の店舗-店舗グループマッピングを取得（認証付き）
 */
export async function getSellerSellerGroupMapCurrentAuthenticated(
  accountId: string,
  authHeaders: Record<string, string>,
): Promise<MiseSellerSellerGroupMap[]> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    return await client.get<MiseSellerSellerGroupMap[]>(
      `/mise/accounts/${accountId}/seller-seller-group-map/current`,
    );
  } catch (error) {
    console.error(
      `Failed to fetch current seller-seller group map for account ${accountId}:`,
      error,
    );
    throw error;
  }
}

/**
 * 集計前の店舗-店舗グループマッピングを取得（認証付き）
 */
export async function getSellerSellerGroupMapNextAuthenticated(
  accountId: string,
  authHeaders: Record<string, string>,
): Promise<MiseSellerSellerGroupMap[]> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    return await client.get<MiseSellerSellerGroupMap[]>(
      `/mise/accounts/${accountId}/seller-seller-group-map/next`,
    );
  } catch (error) {
    console.error(`Failed to fetch next seller-seller group map for account ${accountId}:`, error);
    throw error;
  }
}

/**
 * 集計前の店舗-店舗グループマッピングを更新（認証付き）
 */
export async function updateSellerSellerGroupMapNextAuthenticated(
  accountId: string,
  request: UpdateSellerSellerGroupMapNextRequest,
  authHeaders: Record<string, string>,
): Promise<void> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    await client.put(`/mise/accounts/${accountId}/seller-seller-group-map/next`, request);
  } catch (error) {
    console.error(`Failed to update next seller-seller group map for account ${accountId}:`, error);
    throw error;
  }
}
